// src/hooks/api/usePlayers.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { playersService } from "@/api/services/player.service";
import type {
  CreatePlayerRequest,
  UpdatePlayerRequest,
  PlayerFilters,
} from "@/types/api.types";

// Query Keys
export const playerKeys = {
  all: ["players"] as const,
  lists: () => [...playerKeys.all, "list"] as const,
  list: (filters?: PlayerFilters) => [...playerKeys.lists(), filters] as const,
  details: () => [...playerKeys.all, "detail"] as const,
  detail: (id: number) => [...playerKeys.details(), id] as const,
  teams: (id: number) => [...playerKeys.detail(id), "teams"] as const,
};

// Get all players with filters
export function usePlayersAdmin(filters?: PlayerFilters) {
  return useQuery({
    queryKey: playerKeys.list(filters),
    queryFn: () => playersService.getAllPlayers(filters),
    refetchInterval: 60000,
  });
}

// Get single player
export function usePlayer(id: number) {
  return useQuery({
    queryKey: playerKeys.detail(id),
    queryFn: () => playersService.getPlayer(id),
    enabled: !!id,
  });
}

// Get player teams
export function usePlayerTeams(id: number) {
  return useQuery({
    queryKey: playerKeys.teams(id),
    queryFn: () => playersService.getPlayerTeams(id),
    enabled: !!id,
  });
}

// Create player
export function useCreatePlayer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePlayerRequest) =>
      playersService.createPlayer(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: playerKeys.lists() });
    },
  });
}

// Update player
export function useUpdatePlayer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdatePlayerRequest }) =>
      playersService.updatePlayer(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: playerKeys.lists() });
    },
  });
}

// Delete player
export function useDeletePlayer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => playersService.deletePlayer(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: playerKeys.lists() });
    },
  });
}

// Toggle active status
export function useTogglePlayerActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      isActive
        ? playersService.activatePlayer(id)
        : playersService.deactivatePlayer(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: playerKeys.lists() });
    },
  });
}
